/**
 * Drawer (sheet) — a Radix Dialog whose content slides in from a screen edge.
 *
 * Shares the Dialog Root/Trigger/Close/Title/Description parts (re-exported with
 * `Drawer*` names) and overrides only the Content positioning.
 */
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactElement,
} from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { cn } from './cn.js';
import {
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogDescription,
} from './Dialog.js';

export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;
export const DrawerTitle = DialogTitle;
export const DrawerDescription = DialogDescription;

export type DrawerSide = 'left' | 'right';

const sideClasses: Record<DrawerSide, string> = {
  right:
    'right-0 top-0 h-full w-[min(24rem,calc(100vw-2rem))] border-l data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
  left:
    'left-0 top-0 h-full w-[min(24rem,calc(100vw-2rem))] border-r data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
};

export interface DrawerContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: DrawerSide;
  hideClose?: boolean;
}

export const DrawerContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(({ className, children, side = 'right', hideClose, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed z-50 flex flex-col gap-4 overflow-y-auto border-border bg-surface p-5 text-text shadow-overlay',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        sideClasses[side],
        className,
      )}
      {...props}
    >
      {children}
      {!hideClose ? (
        <DialogPrimitive.Close
          aria-label="Close"
          className={cn(
            'absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted',
            'transition-colors hover:bg-surface-2 hover:text-text',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          )}
        >
          <CloseIcon />
        </DialogPrimitive.Close>
      ) : null}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DrawerContent.displayName = 'DrawerContent';

function CloseIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
